import type {ProtectedResourceMetadata} from '@microsoft/agent-host-protocol'
import type {AhpClient} from '@microsoft/agent-host-protocol/client'

/** localStorage key holding a GitHub token for the AHP `authenticate` handshake. */
export const GITHUB_TOKEN_STORAGE_KEY = 'ahp_github_token'

/** AHP error code returned when the host requires authentication for a protected resource. */
export const AUTH_REQUIRED_CODE = -32007

/** Resolves a bearer token for an AHP protected resource; `undefined` leaves it unauthenticated. */
export type ResourceTokenProvider = (resource: string) => Promise<string | undefined> | string | undefined

/** Hosts whose protected resources may receive the GitHub token. */
export function isGitHubResource(resource: string): boolean {
  const match = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(resource)
  if (!match) return false
  const authority = match[1] ?? ''
  const host = (authority.split('@').pop() ?? '').split(':')[0]?.toLowerCase() ?? ''
  return (
    host === 'github.com' ||
    host.endsWith('.github.com') ||
    host === 'githubcopilot.com' ||
    host.endsWith('.githubcopilot.com') ||
    host.endsWith('.ghe.com')
  )
}

export function defaultResourceTokenProvider(resource: string): string | undefined {
  if (!isGitHubResource(resource)) return undefined
  try {
    return globalThis.localStorage?.getItem(GITHUB_TOKEN_STORAGE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

export function authRequiredResources(data: unknown): ProtectedResourceMetadata[] {
  const resources = (data as {resources?: ProtectedResourceMetadata[]} | undefined)?.resources
  return Array.isArray(resources) ? resources : []
}

/** Push tokens for each required resource the provider can satisfy; returns true if any was authenticated. */
export async function satisfyResources(
  client: AhpClient,
  resources: ProtectedResourceMetadata[],
  provider: ResourceTokenProvider,
): Promise<boolean> {
  let satisfied = false
  for (const meta of resources) {
    if (meta.required === false) continue
    const token = await provider(meta.resource)
    if (!token) continue
    await client.request('authenticate', {channel: 'ahp-root://', resource: meta.resource, token})
    satisfied = true
  }
  return satisfied
}
